/**
 * WCC Historical Stats Modal
 *
 * Displays comprehensive WCC strategy statistics calculated from historical data.
 * Features time presets (1d, 3d, 1w, 1m) and full analysis breakdown.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ColumnDef } from '@tanstack/react-table';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { normalizeToBaseSymbol, StrategySymbol } from '../store/settingsStore';
import { useMarketStore } from '../store/marketStore';
import { DataTable } from './DataTable/DataTable';
import './WccHistoricalStatsModal.css';

interface WccHistoricalStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TimePreset = '1d' | '3d' | '1w' | '1m';
type TabType = 'overview' | 'sessions' | 'confluence' | 'confidence' | 'takeit' | 'streaks' | 'log' | 'calendar';

interface CoreStats {
  totalSignals: number;
  tradedSignals: number;
  skippedSignals: number;
  wins: number;
  losses: number;
  openTrades: number;
  winRate: number;
  totalPnl: number;
  avgWin: number;
  avgLoss: number;
  largestWin: number;
  largestLoss: number;
  profitFactor: number;
  expectancy: number;
}

interface SessionStats {
  asian: CoreStats;
  london: CoreStats;
  newYork: CoreStats;
  overnight: CoreStats;
}

interface ConfluenceStats {
  negative: CoreStats;
  none: CoreStats;
  low: CoreStats;
  medium: CoreStats;
  high: CoreStats;
}

// Confidence score pivot (signal strength percentage)
interface ConfidenceStats {
  veryLow: CoreStats;   // 0-20%
  low: CoreStats;       // 21-40%
  medium: CoreStats;    // 41-60%
  high: CoreStats;      // 61-80%
  veryHigh: CoreStats;  // 81-100%
}

// Take It score pivot (confidence + confluence combined)
interface TakeItScoreStats {
  veryLow: CoreStats;   // 0-40
  low: CoreStats;       // 41-60
  medium: CoreStats;    // 61-80
  high: CoreStats;      // 81-100
  veryHigh: CoreStats;  // 101+
}

interface TakeItStats {
  threshold: number;
  aboveThreshold: CoreStats;
  belowThreshold: CoreStats;
}

interface DrawdownStats {
  maxDrawdown: number;
  maxDrawdownPct: number;
  currentDrawdown: number;
  drawdownPeriods: number;
  avgRecoveryTrades: number;
}

interface StreakStats {
  currentWinStreak: number;
  currentLoseStreak: number;
  maxWinStreak: number;
  maxLoseStreak: number;
  avgWinStreak: number;
  avgLoseStreak: number;
}

interface TradeResult {
  timestamp: number;
  direction: string;
  entryPrice: number;
  exitPrice: number | null;
  outcome: string;
  pnl: number;
  barsToExit: number;
  confidence: number | null;
  confluenceScore: number | null;
  confluenceSymbols: string[] | null;
  takeItScore: number;  // Combined score from API (confidence + confluenceScore)
  session: string;
  hour: number;
  skipped: boolean;
  skipReason: string | null;
  mlScore: number | null;  // ML model probability score 0-100
  mlRecommendation: 'TAKE' | 'SKIP' | null;  // ML model recommendation
}

interface WccStats {
  symbol: string;
  preset: string;
  fromTimestamp: number;
  toTimestamp: number;
  tpPoints: number;
  slPoints: number;
  takeItThreshold: number;
  core: CoreStats;
  byDirection: {
    bullish: CoreStats;
    bearish: CoreStats;
  };
  bySession: SessionStats;
  byHour: Record<string, CoreStats>;
  byConfluence: ConfluenceStats;
  byConfidence: ConfidenceStats;
  byTakeItScore: TakeItScoreStats;
  takeIt: TakeItStats;
  drawdown: DrawdownStats;
  streaks: StreakStats;
  trades: TradeResult[];
}

const PRESETS: { value: TimePreset; label: string }[] = [
  { value: '1w', label: '1 Week' },
  { value: '1m', label: '1 Month' },
];

// Quick date filter options
type QuickFilter = '24h' | '2d' | 'custom' | null;

// Helper functions to recalculate stats from filtered trades
const calcCoreStats = (trades: TradeResult[]): CoreStats => {
  const totalSignals = trades.length;
  const skippedTrades = trades.filter(t => t.skipped);
  const tradedTrades = trades.filter(t => !t.skipped);
  const closedTrades = tradedTrades.filter(t => t.outcome === 'WIN' || t.outcome === 'LOSS');
  const wins = closedTrades.filter(t => t.outcome === 'WIN').length;
  const losses = closedTrades.filter(t => t.outcome === 'LOSS').length;
  const openTrades = tradedTrades.filter(t => t.outcome === 'OPEN').length;

  const winRate = closedTrades.length > 0 ? (wins / closedTrades.length) * 100 : 0;
  const totalPnl = closedTrades.reduce((sum, t) => sum + t.pnl, 0);

  const winPnls = closedTrades.filter(t => t.pnl > 0).map(t => t.pnl);
  const lossPnls = closedTrades.filter(t => t.pnl < 0).map(t => t.pnl);

  const avgWin = winPnls.length > 0 ? winPnls.reduce((a, b) => a + b, 0) / winPnls.length : 0;
  const avgLoss = lossPnls.length > 0 ? Math.abs(lossPnls.reduce((a, b) => a + b, 0) / lossPnls.length) : 0;
  const largestWin = winPnls.length > 0 ? Math.max(...winPnls) : 0;
  const largestLoss = lossPnls.length > 0 ? Math.abs(Math.min(...lossPnls)) : 0;

  const totalWins = winPnls.reduce((a, b) => a + b, 0);
  const totalLosses = Math.abs(lossPnls.reduce((a, b) => a + b, 0));
  const profitFactor = totalLosses > 0 ? totalWins / totalLosses : totalWins > 0 ? Infinity : 0;
  const expectancy = closedTrades.length > 0 ? totalPnl / closedTrades.length : 0;

  return {
    totalSignals,
    tradedSignals: tradedTrades.length,
    skippedSignals: skippedTrades.length,
    wins,
    losses,
    openTrades,
    winRate,
    totalPnl,
    avgWin,
    avgLoss,
    largestWin,
    largestLoss,
    profitFactor: isFinite(profitFactor) ? profitFactor : 0,
    expectancy,
  };
};

const calcDirectionStats = (trades: TradeResult[]) => ({
  bullish: calcCoreStats(trades.filter(t => t.direction === 'LONG')),
  bearish: calcCoreStats(trades.filter(t => t.direction === 'SHORT')),
});

const calcSessionStats = (trades: TradeResult[]): SessionStats => ({
  asian: calcCoreStats(trades.filter(t => t.session.toLowerCase() === 'asian')),
  london: calcCoreStats(trades.filter(t => t.session.toLowerCase() === 'london')),
  newYork: calcCoreStats(trades.filter(t => t.session.toLowerCase() === 'newyork')),
  overnight: calcCoreStats(trades.filter(t => t.session.toLowerCase() === 'overnight')),
});

const calcHourlyStats = (trades: TradeResult[]): Record<string, CoreStats> => {
  const byHour: Record<string, CoreStats> = {};
  for (let h = 0; h < 24; h++) {
    const hourTrades = trades.filter(t => t.hour === h);
    if (hourTrades.length > 0) {
      byHour[h.toString()] = calcCoreStats(hourTrades);
    }
  }
  return byHour;
};

const calcConfluenceStats = (trades: TradeResult[]): ConfluenceStats => ({
  negative: calcCoreStats(trades.filter(t => (t.confluenceScore ?? 0) < 0)),
  none: calcCoreStats(trades.filter(t => t.confluenceScore === 0 || t.confluenceScore === null)),
  low: calcCoreStats(trades.filter(t => (t.confluenceScore ?? 0) > 0 && (t.confluenceScore ?? 0) <= 30)),
  medium: calcCoreStats(trades.filter(t => (t.confluenceScore ?? 0) > 30 && (t.confluenceScore ?? 0) <= 60)),
  high: calcCoreStats(trades.filter(t => (t.confluenceScore ?? 0) > 60)),
});

const calcConfidenceStats = (trades: TradeResult[]): ConfidenceStats => ({
  veryLow: calcCoreStats(trades.filter(t => (t.confidence ?? 0) >= 0 && (t.confidence ?? 0) <= 20)),
  low: calcCoreStats(trades.filter(t => (t.confidence ?? 0) > 20 && (t.confidence ?? 0) <= 40)),
  medium: calcCoreStats(trades.filter(t => (t.confidence ?? 0) > 40 && (t.confidence ?? 0) <= 60)),
  high: calcCoreStats(trades.filter(t => (t.confidence ?? 0) > 60 && (t.confidence ?? 0) <= 80)),
  veryHigh: calcCoreStats(trades.filter(t => (t.confidence ?? 0) > 80)),
});

const calcTakeItScoreStats = (trades: TradeResult[]): TakeItScoreStats => ({
  veryLow: calcCoreStats(trades.filter(t => t.takeItScore >= 0 && t.takeItScore <= 40)),
  low: calcCoreStats(trades.filter(t => t.takeItScore > 40 && t.takeItScore <= 60)),
  medium: calcCoreStats(trades.filter(t => t.takeItScore > 60 && t.takeItScore <= 80)),
  high: calcCoreStats(trades.filter(t => t.takeItScore > 80 && t.takeItScore <= 100)),
  veryHigh: calcCoreStats(trades.filter(t => t.takeItScore > 100)),
});

const calcTakeItStats = (trades: TradeResult[], threshold: number): TakeItStats => {
  // Use takeItScore from API (confidence + confluenceScore), only for non-skipped trades
  const tradedOnly = trades.filter(t => !t.skipped);
  return {
    threshold,
    aboveThreshold: calcCoreStats(tradedOnly.filter(t => t.takeItScore > threshold)),
    belowThreshold: calcCoreStats(tradedOnly.filter(t => t.takeItScore <= threshold)),
  };
};

const calcDrawdownStats = (trades: TradeResult[]): DrawdownStats => {
  const closedTrades = trades.filter(t => !t.skipped && (t.outcome === 'WIN' || t.outcome === 'LOSS'));
  if (closedTrades.length === 0) {
    return { maxDrawdown: 0, maxDrawdownPct: 0, currentDrawdown: 0, drawdownPeriods: 0, avgRecoveryTrades: 0 };
  }

  let peak = 0, cumPnl = 0, maxDrawdown = 0, currentDrawdown = 0;
  let drawdownPeriods = 0, inDrawdown = false;
  const recoveryTrades: number[] = [];
  let tradesSincePeak = 0;

  for (const trade of closedTrades) {
    cumPnl += trade.pnl;
    tradesSincePeak++;

    if (cumPnl > peak) {
      if (inDrawdown && tradesSincePeak > 1) {
        recoveryTrades.push(tradesSincePeak);
      }
      peak = cumPnl;
      tradesSincePeak = 0;
      inDrawdown = false;
    } else {
      const dd = peak - cumPnl;
      if (dd > maxDrawdown) maxDrawdown = dd;
      if (!inDrawdown && dd > 0) {
        inDrawdown = true;
        drawdownPeriods++;
      }
    }
  }

  currentDrawdown = peak - cumPnl;
  const avgRecoveryTrades = recoveryTrades.length > 0
    ? recoveryTrades.reduce((a, b) => a + b, 0) / recoveryTrades.length
    : 0;

  return {
    maxDrawdown,
    maxDrawdownPct: peak > 0 ? (maxDrawdown / peak) * 100 : 0,
    currentDrawdown,
    drawdownPeriods,
    avgRecoveryTrades,
  };
};

const calcStreakStats = (trades: TradeResult[]): StreakStats => {
  const closedTrades = trades.filter(t => !t.skipped && (t.outcome === 'WIN' || t.outcome === 'LOSS'));
  if (closedTrades.length === 0) {
    return { currentWinStreak: 0, currentLoseStreak: 0, maxWinStreak: 0, maxLoseStreak: 0, avgWinStreak: 0, avgLoseStreak: 0 };
  }

  let currentWinStreak = 0, currentLoseStreak = 0;
  let maxWinStreak = 0, maxLoseStreak = 0;
  const winStreaks: number[] = [], loseStreaks: number[] = [];
  let streak = 0, lastOutcome: string | null = null;

  for (const trade of closedTrades) {
    if (trade.outcome === lastOutcome) {
      streak++;
    } else {
      if (lastOutcome === 'WIN' && streak > 0) winStreaks.push(streak);
      if (lastOutcome === 'LOSS' && streak > 0) loseStreaks.push(streak);
      streak = 1;
      lastOutcome = trade.outcome;
    }

    if (trade.outcome === 'WIN') {
      maxWinStreak = Math.max(maxWinStreak, streak);
    } else {
      maxLoseStreak = Math.max(maxLoseStreak, streak);
    }
  }

  // Final streak
  if (lastOutcome === 'WIN') {
    winStreaks.push(streak);
    currentWinStreak = streak;
    currentLoseStreak = 0;
  } else if (lastOutcome === 'LOSS') {
    loseStreaks.push(streak);
    currentLoseStreak = streak;
    currentWinStreak = 0;
  }

  return {
    currentWinStreak,
    currentLoseStreak,
    maxWinStreak,
    maxLoseStreak,
    avgWinStreak: winStreaks.length > 0 ? winStreaks.reduce((a, b) => a + b, 0) / winStreaks.length : 0,
    avgLoseStreak: loseStreaks.length > 0 ? loseStreaks.reduce((a, b) => a + b, 0) / loseStreaks.length : 0,
  };
};

export const WccHistoricalStatsModal: React.FC<WccHistoricalStatsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [preset, setPreset] = useState<TimePreset>('1w');
  const [stats, setStats] = useState<WccStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Custom TP/SL for backtesting - null means use server defaults
  const [customTp, setCustomTp] = useState<number | null>(null);
  const [customSl, setCustomSl] = useState<number | null>(null);

  // Global date range filter (filters trades across all tabs)
  const [globalStartDate, setGlobalStartDate] = useState<Date | null>(null);
  const [globalEndDate, setGlobalEndDate] = useState<Date | null>(null);
  const [quickFilter, setQuickFilter] = useState<QuickFilter>(null);

  // Selected date from calendar (for filtering log)
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Take It Only filter - show only signals with confluence >= threshold
  const [takeItOnly, setTakeItOnly] = useState(false);

  const { symbol } = useMarketStore();
  const baseSymbol = normalizeToBaseSymbol(symbol || '') as StrategySymbol | null;

  // Get dollar per point based on symbol (MNQ=$2, NQ=$20, MES=$1.25, ES=$12.50)
  const dollarPerPoint = baseSymbol === 'NQ' ? 20 :
                         baseSymbol === 'ES' ? 12.5 :
                         baseSymbol === 'MES' ? 1.25 : 2; // MNQ default

  // Fetch stats when modal opens or preset changes
  const fetchStats = useCallback(async () => {
    if (!isOpen) return;

    setIsLoading(true);
    setError(null);

    try {
      // Determine backend URL based on environment
      const isLocalhost = window.location.hostname === 'localhost' ||
                          window.location.hostname === '127.0.0.1';
      const isStaging = window.location.port === '4002' ||
                        window.location.hostname.includes('staging');

      let baseUrl: string;
      if (isLocalhost) {
        baseUrl = isStaging ? 'http://localhost:9647' : 'http://localhost:9547';
      } else {
        baseUrl = isStaging
          ? 'https://staging-uttapi.ginuga.com'
          : 'https://uttapi.ginuga.com';
      }

      const url = new URL(`${baseUrl}/api/wcc-stats/historical`);
      url.searchParams.set('preset', preset);
      if (symbol) url.searchParams.set('symbol', symbol);
      if (customTp !== null) url.searchParams.set('tp', customTp.toString());
      if (customSl !== null) url.searchParams.set('sl', customSl.toString());

      const response = await fetch(url.toString());
      const data = await response.json();

      if (data.success) {
        setStats(data.stats);
      } else {
        setError(data.message || 'Failed to fetch stats');
      }
    } catch (err) {
      setError('Failed to connect to server');
      console.error('Error fetching WCC stats:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isOpen, preset, symbol, customTp, customSl]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const formatCurrency = useCallback((value: number | null | undefined) => {
    if (value == null) return '$0';
    const sign = value >= 0 ? '+' : '';
    return `${sign}$${Math.abs(value).toFixed(0)}`;
  }, []);

  const formatPoints = useCallback((value: number | null | undefined) => {
    if (value == null) return '0 pts';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)} pts`;
  }, []);

  const formatPercent = useCallback((value: number | null | undefined) => {
    if (value == null) return '0%';
    return `${value.toFixed(1)}%`;
  }, []);

  // Apply quick filter to set date range
  const applyQuickFilter = useCallback((filter: QuickFilter) => {
    setQuickFilter(filter);
    setSelectedDate(null);

    if (!filter || filter === 'custom') {
      if (filter !== 'custom') {
        setGlobalStartDate(null);
        setGlobalEndDate(null);
      }
      return;
    }

    const now = new Date();
    const endDate = new Date(now);
    endDate.setHours(23, 59, 59, 999);

    const startDate = new Date(now);
    if (filter === '24h') {
      startDate.setHours(startDate.getHours() - 24);
    } else if (filter === '2d') {
      startDate.setDate(startDate.getDate() - 2);
      startDate.setHours(0, 0, 0, 0);
    }

    setGlobalStartDate(startDate);
    setGlobalEndDate(endDate);
  }, []);

  // Filter trades by global date range (or selectedDate from calendar)
  const filteredTrades = useMemo(() => {
    if (!stats?.trades) return [];
    let trades = [...stats.trades];

    // If selectedDate is set (from calendar click), filter to that specific day
    if (selectedDate) {
      // Parse the YYYY-MM-DD string as local date
      const [year, month, day] = selectedDate.split('-').map(Number);
      const dayStart = new Date(year, month - 1, day, 0, 0, 0, 0).getTime();
      const dayEnd = new Date(year, month - 1, day, 23, 59, 59, 999).getTime();
      trades = trades.filter(t => t.timestamp >= dayStart && t.timestamp <= dayEnd);
    } else {
      // Apply global date range filter (only when not filtering by specific date)
      if (globalStartDate) {
        const startTs = globalStartDate.getTime();
        trades = trades.filter(t => t.timestamp >= startTs);
      }
      if (globalEndDate) {
        const endTs = globalEndDate.getTime();
        trades = trades.filter(t => t.timestamp <= endTs);
      }
    }

    // Apply Take It Only filter - show only signals with takeItScore > threshold
    if (takeItOnly) {
      const threshold = stats.takeItThreshold || 80;
      trades = trades.filter(t => !t.skipped && t.takeItScore > threshold);
    }

    return trades;
  }, [stats?.trades, globalStartDate, globalEndDate, selectedDate, takeItOnly, stats?.takeItThreshold]);

  // Create filtered stats object for tabs
  // Recalculate all stats when filters are applied
  const filteredStats = useMemo(() => {
    if (!stats) return null;

    // If no filter applied, use original stats
    const isFiltered = globalStartDate || globalEndDate || selectedDate || quickFilter || takeItOnly;
    if (!isFiltered) {
      return stats;
    }

    // Recalculate all stats from filtered trades
    return {
      ...stats,
      trades: filteredTrades,
      core: calcCoreStats(filteredTrades),
      byDirection: calcDirectionStats(filteredTrades),
      bySession: calcSessionStats(filteredTrades),
      byHour: calcHourlyStats(filteredTrades),
      byConfluence: calcConfluenceStats(filteredTrades),
      byConfidence: calcConfidenceStats(filteredTrades),
      byTakeItScore: calcTakeItScoreStats(filteredTrades),
      takeIt: calcTakeItStats(filteredTrades, stats.takeItThreshold),
      drawdown: calcDrawdownStats(filteredTrades),
      streaks: calcStreakStats(filteredTrades),
    };
  }, [stats, filteredTrades, globalStartDate, globalEndDate, selectedDate, quickFilter, takeItOnly]);

  // Clear all filters helper
  const clearDateFilter = useCallback(() => {
    setGlobalStartDate(null);
    setGlobalEndDate(null);
    setSelectedDate(null);
    setQuickFilter(null);
    setTakeItOnly(false);
  }, []);

  if (!isOpen) return null;

  return createPortal(
    <div className="wcc-stats-modal-overlay" onClick={onClose}>
      <div className="wcc-stats-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="wcc-stats-modal-header">
          <div className="wcc-stats-modal-title">
            WCC Strategy Stats
            {baseSymbol && <span className="wcc-stats-symbol">{baseSymbol}</span>}
            {stats && <span className="wcc-stats-subtitle">{stats.preset}</span>}
          </div>
          <button className="wcc-stats-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Global Filters */}
        <div className="wcc-stats-filters">
          {/* Period & Quick Filters - unified time controls */}
          <div className="wcc-stats-filter-row">
            <div className="wcc-stats-time-controls">
              {/* Quick filters (client-side) */}
              <button
                className={`wcc-stats-time-btn ${quickFilter === '24h' ? 'active' : ''}`}
                onClick={() => applyQuickFilter(quickFilter === '24h' ? null : '24h')}
              >
                24H
              </button>
              <button
                className={`wcc-stats-time-btn ${quickFilter === '2d' ? 'active' : ''}`}
                onClick={() => applyQuickFilter(quickFilter === '2d' ? null : '2d')}
              >
                2D
              </button>

              <span className="wcc-stats-divider" />

              {/* Presets (server load) */}
              {PRESETS.map(p => (
                <button
                  key={p.value}
                  className={`wcc-stats-time-btn preset ${preset === p.value && !quickFilter ? 'active' : ''}`}
                  onClick={() => {
                    setPreset(p.value);
                    setQuickFilter(null);
                    setGlobalStartDate(null);
                    setGlobalEndDate(null);
                    setSelectedDate(null);
                  }}
                  disabled={isLoading}
                >
                  {p.label}
                </button>
              ))}

              <span className="wcc-stats-divider" />

              {/* Take It Only filter */}
              <button
                className={`wcc-stats-time-btn ${takeItOnly ? 'active' : ''}`}
                onClick={() => setTakeItOnly(!takeItOnly)}
                title={`Show only Take It signals (score > ${stats?.takeItThreshold || 80})`}
              >
                Take It
              </button>

              <span className="wcc-stats-divider" />

              {/* Custom date range picker */}
              <DatePicker
                selectsRange
                startDate={globalStartDate}
                endDate={globalEndDate}
                onChange={(dates) => {
                  const [start, end] = dates as [Date | null, Date | null];
                  setSelectedDate(null);
                  setQuickFilter('custom');
                  setGlobalStartDate(start);
                  setGlobalEndDate(end);
                }}
                dateFormat="MMM d"
                placeholderText="Custom"
                className={`wcc-stats-datepicker-range ${quickFilter === 'custom' ? 'active' : ''}`}
                calendarClassName="wcc-stats-calendar"
                popperPlacement="bottom-start"
                isClearable
                monthsShown={2}
              />
            </div>

            {(globalStartDate || globalEndDate || selectedDate || takeItOnly) && (
              <button className="wcc-stats-clear-date" onClick={clearDateFilter}>
                ×
              </button>
            )}

            {filteredTrades.length !== (stats?.trades?.length || 0) && (
              <span className="wcc-stats-filter-count">
                {filteredTrades.length} / {stats?.trades?.length || 0}
              </span>
            )}
          </div>

          {/* TP/SL Controls */}
          <div className="wcc-stats-filter-row secondary">
            <div className="wcc-stats-filter-group">
              <label>TP</label>
              <input
                type="number"
                value={customTp ?? stats?.tpPoints ?? ''}
                onChange={(e) => setCustomTp(e.target.value ? parseFloat(e.target.value) : null)}
                placeholder={stats?.tpPoints?.toString() || '5'}
                min="0.25"
                step="0.25"
                className="wcc-stats-input-number"
              />
              <span className="param-unit">pts</span>
            </div>
            <div className="wcc-stats-filter-group">
              <label>SL</label>
              <input
                type="number"
                value={customSl ?? stats?.slPoints ?? ''}
                onChange={(e) => setCustomSl(e.target.value ? parseFloat(e.target.value) : null)}
                placeholder={stats?.slPoints?.toString() || '25'}
                min="0.25"
                step="0.25"
                className="wcc-stats-input-number"
              />
              <span className="param-unit">pts</span>
            </div>
            <button
              className="wcc-stats-recalc"
              onClick={fetchStats}
              disabled={isLoading}
            >
              {isLoading ? '...' : 'Recalc'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="wcc-stats-tabs">
          <button
            className={`wcc-stats-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`wcc-stats-tab ${activeTab === 'sessions' ? 'active' : ''}`}
            onClick={() => setActiveTab('sessions')}
          >
            Sessions
          </button>
          <button
            className={`wcc-stats-tab ${activeTab === 'confluence' ? 'active' : ''}`}
            onClick={() => setActiveTab('confluence')}
          >
            Confluence
          </button>
          <button
            className={`wcc-stats-tab ${activeTab === 'confidence' ? 'active' : ''}`}
            onClick={() => setActiveTab('confidence')}
          >
            Confidence
          </button>
          <button
            className={`wcc-stats-tab ${activeTab === 'takeit' ? 'active' : ''}`}
            onClick={() => setActiveTab('takeit')}
          >
            Take It
          </button>
          <button
            className={`wcc-stats-tab ${activeTab === 'streaks' ? 'active' : ''}`}
            onClick={() => setActiveTab('streaks')}
          >
            Streaks
          </button>
          <button
            className={`wcc-stats-tab ${activeTab === 'log' ? 'active' : ''}`}
            onClick={() => setActiveTab('log')}
          >
            Log
          </button>
          <button
            className={`wcc-stats-tab ${activeTab === 'calendar' ? 'active' : ''}`}
            onClick={() => setActiveTab('calendar')}
          >
            Calendar
          </button>
        </div>

        {/* Content */}
        <div className="wcc-stats-modal-content">
          {isLoading ? (
            <div className="wcc-stats-loading">Loading...</div>
          ) : error ? (
            <div className="wcc-stats-error">{error}</div>
          ) : filteredStats ? (
            activeTab === 'overview' ? (
              <OverviewTab
                stats={filteredStats}
                dollarPerPoint={dollarPerPoint}
                formatCurrency={formatCurrency}
                formatPoints={formatPoints}
                formatPercent={formatPercent}
              />
            ) : activeTab === 'sessions' ? (
              <SessionsTab
                stats={filteredStats}
                formatCurrency={formatCurrency}
                formatPercent={formatPercent}
              />
            ) : activeTab === 'confluence' ? (
              <ConfluenceTab
                stats={filteredStats}
                formatCurrency={formatCurrency}
                formatPercent={formatPercent}
              />
            ) : activeTab === 'confidence' ? (
              <ConfidenceTab
                stats={filteredStats}
                formatCurrency={formatCurrency}
                formatPercent={formatPercent}
              />
            ) : activeTab === 'takeit' ? (
              <TakeItScoreTab
                stats={filteredStats}
                formatCurrency={formatCurrency}
                formatPercent={formatPercent}
              />
            ) : activeTab === 'streaks' ? (
              <StreaksTab
                stats={filteredStats}
                formatPoints={formatPoints}
              />
            ) : activeTab === 'calendar' ? (
              <CalendarTab
                stats={filteredStats}
                formatPoints={formatPoints}
                onDayClick={(date) => {
                  // Set the selected date and navigate to Log tab
                  setSelectedDate(date);
                  setGlobalStartDate(null);
                  setGlobalEndDate(null);
                  setQuickFilter(null);
                  setActiveTab('log');
                }}
              />
            ) : (
              <LogTab
                stats={filteredStats}
                formatPoints={formatPoints}
              />
            )
          ) : (
            <div className="wcc-stats-empty">No data available</div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

// Overview Tab Component
const OverviewTab: React.FC<{
  stats: WccStats;
  dollarPerPoint: number;
  formatCurrency: (v: number | null | undefined) => string;
  formatPoints: (v: number | null | undefined) => string;
  formatPercent: (v: number | null | undefined) => string;
}> = ({ stats, dollarPerPoint, formatCurrency, formatPoints, formatPercent }) => {
  // Guard against null core stats
  const core = stats.core || {
    totalSignals: 0, tradedSignals: 0, skippedSignals: 0, wins: 0, losses: 0,
    openTrades: 0, winRate: 0, totalPnl: 0, avgWin: 0, avgLoss: 0,
    largestWin: 0, largestLoss: 0, profitFactor: 0, expectancy: 0
  };
  const estimatedPnl = (core.totalPnl || 0) * dollarPerPoint;

  return (
    <div className="wcc-stats-overview">
      {/* Core Stats Grid */}
      <div className="wcc-stats-grid">
        <div className="wcc-stats-card primary">
          <div className="wcc-stats-card-label">Win Rate</div>
          <div className={`wcc-stats-card-value ${(core.winRate || 0) >= 50 ? 'positive' : 'negative'}`}>
            {formatPercent(core.winRate)}
          </div>
          <div className="wcc-stats-card-sub">
            {core.wins}W / {core.losses}L
          </div>
        </div>

        <div className="wcc-stats-card primary">
          <div className="wcc-stats-card-label">Est. P&L</div>
          <div className={`wcc-stats-card-value ${estimatedPnl >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(estimatedPnl)}
          </div>
          <div className="wcc-stats-card-sub">
            {formatPoints(core.totalPnl)}
          </div>
        </div>

        <div className="wcc-stats-card">
          <div className="wcc-stats-card-label">Signals</div>
          <div className="wcc-stats-card-value">{core.totalSignals}</div>
          <div className="wcc-stats-card-sub">
            {core.skippedSignals} skipped
          </div>
        </div>

        <div className="wcc-stats-card">
          <div className="wcc-stats-card-label">Profit Factor</div>
          <div className={`wcc-stats-card-value ${(core.profitFactor || 0) >= 1 ? 'positive' : 'negative'}`}>
            {core.profitFactor == null ? '—' : core.profitFactor === Infinity ? '∞' : core.profitFactor.toFixed(2)}
          </div>
        </div>

        <div className="wcc-stats-card">
          <div className="wcc-stats-card-label">Expectancy</div>
          <div className={`wcc-stats-card-value ${(core.expectancy || 0) >= 0 ? 'positive' : 'negative'}`}>
            {formatPoints(core.expectancy)}
          </div>
        </div>

        <div className="wcc-stats-card">
          <div className="wcc-stats-card-label">Avg Win / Loss</div>
          <div className="wcc-stats-card-value">
            <span className="positive">{(core.avgWin || 0).toFixed(1)}</span>
            {' / '}
            <span className="negative">{Math.abs(core.avgLoss || 0).toFixed(1)}</span>
          </div>
        </div>
      </div>

      {/* Direction Breakdown */}
      <div className="wcc-stats-section">
        <h3>Direction Breakdown</h3>
        <div className="wcc-stats-direction-row">
          <div className="wcc-stats-direction bullish">
            <span className="direction-icon">▲</span>
            <span className="direction-label">Bullish</span>
            <span className="direction-stats">
              {stats.byDirection?.bullish?.wins || 0}W/{stats.byDirection?.bullish?.losses || 0}L
              ({formatPercent(stats.byDirection?.bullish?.winRate)})
            </span>
          </div>
          <div className="wcc-stats-direction bearish">
            <span className="direction-icon">▼</span>
            <span className="direction-label">Bearish</span>
            <span className="direction-stats">
              {stats.byDirection?.bearish?.wins || 0}W/{stats.byDirection?.bearish?.losses || 0}L
              ({formatPercent(stats.byDirection?.bearish?.winRate)})
            </span>
          </div>
        </div>
      </div>

      {/* Take It Stats */}
      <div className="wcc-stats-section">
        <h3>Take It Signals (threshold: {stats.takeItThreshold || 80})</h3>
        <div className="wcc-stats-take-it-row">
          <div className="wcc-stats-take-it above">
            <span className="take-it-label">Above Threshold</span>
            <span className="take-it-stats">
              {stats.takeIt?.aboveThreshold?.wins || 0}W/{stats.takeIt?.aboveThreshold?.losses || 0}L
              ({formatPercent(stats.takeIt?.aboveThreshold?.winRate)})
            </span>
            <span className={`take-it-pnl ${(stats.takeIt?.aboveThreshold?.totalPnl || 0) >= 0 ? 'positive' : 'negative'}`}>
              {formatPoints(stats.takeIt?.aboveThreshold?.totalPnl)}
            </span>
          </div>
          <div className="wcc-stats-take-it below">
            <span className="take-it-label">Below Threshold</span>
            <span className="take-it-stats">
              {stats.takeIt?.belowThreshold?.wins || 0}W/{stats.takeIt?.belowThreshold?.losses || 0}L
              ({formatPercent(stats.takeIt?.belowThreshold?.winRate)})
            </span>
            <span className={`take-it-pnl ${(stats.takeIt?.belowThreshold?.totalPnl || 0) >= 0 ? 'positive' : 'negative'}`}>
              {formatPoints(stats.takeIt?.belowThreshold?.totalPnl)}
            </span>
          </div>
        </div>
      </div>

      {/* Settings Info */}
      <div className="wcc-stats-settings">
        <span>TP: {stats.tpPoints} pts</span>
        <span>SL: {stats.slPoints} pts</span>
        <span>Open: {stats.core.openTrades}</span>
      </div>
    </div>
  );
};

// Sessions Tab Component
const SessionsTab: React.FC<{
  stats: WccStats;
  formatCurrency: (v: number) => string;
  formatPercent: (v: number) => string;
}> = ({ stats, formatCurrency, formatPercent }) => {
  const sessions = [
    { key: 'asian', label: 'Asian', time: '00:00-08:00 UTC', stats: stats.bySession.asian },
    { key: 'london', label: 'London', time: '08:00-13:00 UTC', stats: stats.bySession.london },
    { key: 'newYork', label: 'New York', time: '13:00-21:00 UTC', stats: stats.bySession.newYork },
    { key: 'overnight', label: 'Overnight', time: '21:00-00:00 UTC', stats: stats.bySession.overnight },
  ];

  const dollarPerPoint = 2;

  return (
    <div className="wcc-stats-sessions">
      {sessions.map(session => (
        <div key={session.key} className="wcc-stats-session-card">
          <div className="session-header">
            <span className="session-name">{session.label}</span>
            <span className="session-time">{session.time}</span>
          </div>
          <div className="session-stats">
            <div className="session-stat">
              <span className="stat-label">Signals</span>
              <span className="stat-value">{session.stats.totalSignals}</span>
            </div>
            <div className="session-stat">
              <span className="stat-label">Win Rate</span>
              <span className={`stat-value ${session.stats.winRate >= 50 ? 'positive' : 'negative'}`}>
                {formatPercent(session.stats.winRate)}
              </span>
            </div>
            <div className="session-stat">
              <span className="stat-label">W/L</span>
              <span className="stat-value">
                {session.stats.wins}/{session.stats.losses}
              </span>
            </div>
            <div className="session-stat">
              <span className="stat-label">P&L</span>
              <span className={`stat-value ${session.stats.totalPnl >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(session.stats.totalPnl * dollarPerPoint)}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Confluence Tab Component
const ConfluenceTab: React.FC<{
  stats: WccStats;
  formatCurrency: (v: number) => string;
  formatPercent: (v: number) => string;
}> = ({ stats, formatCurrency, formatPercent }) => {
  const confluenceRanges = [
    { key: 'high', label: 'High (61+)', stats: stats.byConfluence.high, color: '#22c55e' },
    { key: 'medium', label: 'Medium (31-60)', stats: stats.byConfluence.medium, color: '#3b82f6' },
    { key: 'low', label: 'Low (1-30)', stats: stats.byConfluence.low, color: '#eab308' },
    { key: 'none', label: 'None (0)', stats: stats.byConfluence.none, color: '#6b7280' },
    { key: 'negative', label: 'Negative (<0)', stats: stats.byConfluence.negative, color: '#ef4444' },
  ];

  const dollarPerPoint = 2;

  return (
    <div className="wcc-stats-confluence">
      <div className="confluence-header">
        <p>Performance breakdown by confluence score range</p>
      </div>
      <div className="confluence-grid">
        {confluenceRanges.map(range => (
          <div key={range.key} className="confluence-card" style={{ borderColor: range.color }}>
            <div className="confluence-card-header" style={{ backgroundColor: range.color }}>
              {range.label}
            </div>
            <div className="confluence-card-stats">
              <div className="confluence-stat">
                <span className="stat-label">Signals</span>
                <span className="stat-value">{range.stats.totalSignals}</span>
              </div>
              <div className="confluence-stat">
                <span className="stat-label">Win Rate</span>
                <span className={`stat-value ${range.stats.winRate >= 50 ? 'positive' : 'negative'}`}>
                  {formatPercent(range.stats.winRate)}
                </span>
              </div>
              <div className="confluence-stat">
                <span className="stat-label">W/L</span>
                <span className="stat-value">
                  {range.stats.wins}/{range.stats.losses}
                </span>
              </div>
              <div className="confluence-stat">
                <span className="stat-label">P&L</span>
                <span className={`stat-value ${range.stats.totalPnl >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(range.stats.totalPnl * dollarPerPoint)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Confidence Tab Component (signal strength percentage)
const ConfidenceTab: React.FC<{
  stats: WccStats;
  formatCurrency: (v: number) => string;
  formatPercent: (v: number) => string;
}> = ({ stats, formatCurrency, formatPercent }) => {
  const confidenceRanges = [
    { key: 'veryHigh', label: 'Very High (81-100%)', stats: stats.byConfidence?.veryHigh, color: '#22c55e' },
    { key: 'high', label: 'High (61-80%)', stats: stats.byConfidence?.high, color: '#10b981' },
    { key: 'medium', label: 'Medium (41-60%)', stats: stats.byConfidence?.medium, color: '#3b82f6' },
    { key: 'low', label: 'Low (21-40%)', stats: stats.byConfidence?.low, color: '#eab308' },
    { key: 'veryLow', label: 'Very Low (0-20%)', stats: stats.byConfidence?.veryLow, color: '#ef4444' },
  ];

  const dollarPerPoint = 2;

  // Filter out empty ranges
  const nonEmptyRanges = confidenceRanges.filter(r => r.stats && r.stats.totalSignals > 0);

  return (
    <div className="wcc-stats-confluence">
      <div className="confluence-header">
        <p>Performance breakdown by signal confidence percentage</p>
      </div>
      <div className="confluence-grid">
        {nonEmptyRanges.length > 0 ? nonEmptyRanges.map(range => (
          <div key={range.key} className="confluence-card" style={{ borderColor: range.color }}>
            <div className="confluence-card-header" style={{ backgroundColor: range.color }}>
              {range.label}
            </div>
            <div className="confluence-card-stats">
              <div className="confluence-stat">
                <span className="stat-label">Signals</span>
                <span className="stat-value">{range.stats?.totalSignals || 0}</span>
              </div>
              <div className="confluence-stat">
                <span className="stat-label">Win Rate</span>
                <span className={`stat-value ${(range.stats?.winRate || 0) >= 50 ? 'positive' : 'negative'}`}>
                  {formatPercent(range.stats?.winRate || 0)}
                </span>
              </div>
              <div className="confluence-stat">
                <span className="stat-label">W/L</span>
                <span className="stat-value">
                  {range.stats?.wins || 0}/{range.stats?.losses || 0}
                </span>
              </div>
              <div className="confluence-stat">
                <span className="stat-label">P&L</span>
                <span className={`stat-value ${(range.stats?.totalPnl || 0) >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency((range.stats?.totalPnl || 0) * dollarPerPoint)}
                </span>
              </div>
            </div>
          </div>
        )) : (
          <div className="wcc-stats-empty">No confidence data available</div>
        )}
      </div>
    </div>
  );
};

// Take It Score Tab Component (confidence + confluence combined)
const TakeItScoreTab: React.FC<{
  stats: WccStats;
  formatCurrency: (v: number) => string;
  formatPercent: (v: number) => string;
}> = ({ stats, formatCurrency, formatPercent }) => {
  const takeItRanges = [
    { key: 'veryHigh', label: 'Very High (101+)', stats: stats.byTakeItScore?.veryHigh, color: '#22c55e' },
    { key: 'high', label: 'High (81-100)', stats: stats.byTakeItScore?.high, color: '#10b981' },
    { key: 'medium', label: 'Medium (61-80)', stats: stats.byTakeItScore?.medium, color: '#3b82f6' },
    { key: 'low', label: 'Low (41-60)', stats: stats.byTakeItScore?.low, color: '#eab308' },
    { key: 'veryLow', label: 'Very Low (0-40)', stats: stats.byTakeItScore?.veryLow, color: '#ef4444' },
  ];

  const dollarPerPoint = 2;
  const threshold = stats.takeItThreshold || 80;

  // Filter out empty ranges
  const nonEmptyRanges = takeItRanges.filter(r => r.stats && r.stats.totalSignals > 0);

  return (
    <div className="wcc-stats-confluence">
      <div className="confluence-header">
        <p>Performance breakdown by Take It score (confidence + confluence). Threshold: {threshold}</p>
      </div>
      <div className="confluence-grid">
        {nonEmptyRanges.length > 0 ? nonEmptyRanges.map(range => (
          <div key={range.key} className="confluence-card" style={{ borderColor: range.color }}>
            <div className="confluence-card-header" style={{ backgroundColor: range.color }}>
              {range.label}
            </div>
            <div className="confluence-card-stats">
              <div className="confluence-stat">
                <span className="stat-label">Signals</span>
                <span className="stat-value">{range.stats?.totalSignals || 0}</span>
              </div>
              <div className="confluence-stat">
                <span className="stat-label">Win Rate</span>
                <span className={`stat-value ${(range.stats?.winRate || 0) >= 50 ? 'positive' : 'negative'}`}>
                  {formatPercent(range.stats?.winRate || 0)}
                </span>
              </div>
              <div className="confluence-stat">
                <span className="stat-label">W/L</span>
                <span className="stat-value">
                  {range.stats?.wins || 0}/{range.stats?.losses || 0}
                </span>
              </div>
              <div className="confluence-stat">
                <span className="stat-label">P&L</span>
                <span className={`stat-value ${(range.stats?.totalPnl || 0) >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency((range.stats?.totalPnl || 0) * dollarPerPoint)}
                </span>
              </div>
            </div>
          </div>
        )) : (
          <div className="wcc-stats-empty">No Take It score data available</div>
        )}
      </div>
    </div>
  );
};

// Streaks Tab Component
const StreaksTab: React.FC<{
  stats: WccStats;
  formatPoints: (v: number) => string;
}> = ({ stats, formatPoints }) => {
  return (
    <div className="wcc-stats-streaks">
      {/* Current Streaks */}
      <div className="streaks-section">
        <h3>Current Streak</h3>
        <div className="streaks-current">
          {stats.streaks.currentWinStreak > 0 ? (
            <div className="streak-badge winning">
              {stats.streaks.currentWinStreak} Win Streak
            </div>
          ) : stats.streaks.currentLoseStreak > 0 ? (
            <div className="streak-badge losing">
              {stats.streaks.currentLoseStreak} Loss Streak
            </div>
          ) : (
            <div className="streak-badge neutral">No Active Streak</div>
          )}
        </div>
      </div>

      {/* Streak Records */}
      <div className="streaks-section">
        <h3>Streak Records</h3>
        <div className="streaks-grid">
          <div className="streak-card winning">
            <div className="streak-card-label">Max Win Streak</div>
            <div className="streak-card-value">{stats.streaks.maxWinStreak}</div>
            <div className="streak-card-sub">
              Avg: {stats.streaks.avgWinStreak.toFixed(1)}
            </div>
          </div>
          <div className="streak-card losing">
            <div className="streak-card-label">Max Loss Streak</div>
            <div className="streak-card-value">{stats.streaks.maxLoseStreak}</div>
            <div className="streak-card-sub">
              Avg: {stats.streaks.avgLoseStreak.toFixed(1)}
            </div>
          </div>
        </div>
      </div>

      {/* Drawdown Stats */}
      <div className="streaks-section">
        <h3>Drawdown Analysis</h3>
        <div className="drawdown-stats">
          <div className="drawdown-stat">
            <span className="stat-label">Max Drawdown</span>
            <span className="stat-value negative">{formatPoints(-stats.drawdown.maxDrawdown)}</span>
          </div>
          <div className="drawdown-stat">
            <span className="stat-label">Current Drawdown</span>
            <span className={`stat-value ${stats.drawdown.currentDrawdown > 0 ? 'negative' : ''}`}>
              {formatPoints(-stats.drawdown.currentDrawdown)}
            </span>
          </div>
          <div className="drawdown-stat">
            <span className="stat-label">Drawdown Periods</span>
            <span className="stat-value">{stats.drawdown.drawdownPeriods}</span>
          </div>
          <div className="drawdown-stat">
            <span className="stat-label">Avg Recovery</span>
            <span className="stat-value">{stats.drawdown.avgRecoveryTrades.toFixed(1)} trades</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Calendar Tab Component
interface DayStats {
  date: string; // YYYY-MM-DD
  allSignals: {
    total: number;
    wins: number;
    losses: number;
    pnl: number;
  };
  takeItSignals: {
    total: number;
    wins: number;
    losses: number;
    pnl: number;
  };
}

const CalendarTab: React.FC<{
  stats: WccStats;
  formatPoints: (v: number) => string;
  onDayClick: (date: string) => void;
}> = ({ stats, formatPoints, onDayClick }) => {
  const threshold = stats.takeItThreshold || 80;

  // Group trades by date and calculate stats
  const dayStats = useMemo(() => {
    const byDate = new Map<string, DayStats>();

    (stats.trades || []).forEach(trade => {
      // Convert to PST date
      const date = new Date(trade.timestamp);
      const pstDate = date.toLocaleDateString('en-CA', {
        timeZone: 'America/Los_Angeles',
      });

      if (!byDate.has(pstDate)) {
        byDate.set(pstDate, {
          date: pstDate,
          allSignals: { total: 0, wins: 0, losses: 0, pnl: 0 },
          takeItSignals: { total: 0, wins: 0, losses: 0, pnl: 0 },
        });
      }

      const day = byDate.get(pstDate)!;

      // Only count non-skipped trades
      if (!trade.skipped) {
        day.allSignals.total++;
        if (trade.outcome.toLowerCase() === 'win') {
          day.allSignals.wins++;
          day.allSignals.pnl += trade.pnl;
        } else if (trade.outcome.toLowerCase() === 'loss') {
          day.allSignals.losses++;
          day.allSignals.pnl += trade.pnl;
        }

        // Check if Take-It signal (takeItScore > threshold)
        if (trade.takeItScore > threshold) {
          day.takeItSignals.total++;
          if (trade.outcome.toLowerCase() === 'win') {
            day.takeItSignals.wins++;
            day.takeItSignals.pnl += trade.pnl;
          } else if (trade.outcome.toLowerCase() === 'loss') {
            day.takeItSignals.losses++;
            day.takeItSignals.pnl += trade.pnl;
          }
        }
      }
    });

    // Sort by date descending
    return Array.from(byDate.values()).sort((a, b) => b.date.localeCompare(a.date));
  }, [stats.trades, threshold]);

  // Calculate totals
  const totals = useMemo(() => {
    return dayStats.reduce(
      (acc, day) => ({
        allSignals: {
          total: acc.allSignals.total + day.allSignals.total,
          wins: acc.allSignals.wins + day.allSignals.wins,
          losses: acc.allSignals.losses + day.allSignals.losses,
          pnl: acc.allSignals.pnl + day.allSignals.pnl,
        },
        takeItSignals: {
          total: acc.takeItSignals.total + day.takeItSignals.total,
          wins: acc.takeItSignals.wins + day.takeItSignals.wins,
          losses: acc.takeItSignals.losses + day.takeItSignals.losses,
          pnl: acc.takeItSignals.pnl + day.takeItSignals.pnl,
        },
      }),
      {
        allSignals: { total: 0, wins: 0, losses: 0, pnl: 0 },
        takeItSignals: { total: 0, wins: 0, losses: 0, pnl: 0 },
      }
    );
  }, [dayStats]);

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="wcc-stats-calendar">
      {/* Summary Cards */}
      <div className="calendar-summary">
        <div className="calendar-summary-card">
          <div className="summary-title">All Signals</div>
          <div className="summary-stats">
            <span className="summary-count">{totals.allSignals.total} trades</span>
            <span className="summary-wl">
              <span className="win">{totals.allSignals.wins}W</span>
              {' / '}
              <span className="loss">{totals.allSignals.losses}L</span>
            </span>
            <span className={`summary-pnl ${totals.allSignals.pnl >= 0 ? 'positive' : 'negative'}`}>
              {formatPoints(totals.allSignals.pnl)}
            </span>
          </div>
        </div>
        <div className="calendar-summary-card takeit">
          <div className="summary-title">Take-It Signals ({threshold}+)</div>
          <div className="summary-stats">
            <span className="summary-count">{totals.takeItSignals.total} trades</span>
            <span className="summary-wl">
              <span className="win">{totals.takeItSignals.wins}W</span>
              {' / '}
              <span className="loss">{totals.takeItSignals.losses}L</span>
            </span>
            <span className={`summary-pnl ${totals.takeItSignals.pnl >= 0 ? 'positive' : 'negative'}`}>
              {formatPoints(totals.takeItSignals.pnl)}
            </span>
          </div>
        </div>
      </div>

      {/* Day List */}
      <div className="calendar-days">
        <div className="calendar-days-header">
          <span className="day-col date">Date</span>
          <span className="day-col signals">All Signals</span>
          <span className="day-col signals">Take-It</span>
        </div>
        {dayStats.map(day => (
          <div
            key={day.date}
            className="calendar-day-row"
            onClick={() => onDayClick(day.date)}
          >
            <span className="day-col date">{formatDate(day.date)}</span>
            <span className="day-col signals">
              <span className="day-wl">
                {day.allSignals.wins}W/{day.allSignals.losses}L
              </span>
              <span className={`day-pnl ${day.allSignals.pnl >= 0 ? 'positive' : 'negative'}`}>
                {formatPoints(day.allSignals.pnl)}
              </span>
            </span>
            <span className="day-col signals">
              {day.takeItSignals.total > 0 ? (
                <>
                  <span className="day-wl">
                    {day.takeItSignals.wins}W/{day.takeItSignals.losses}L
                  </span>
                  <span className={`day-pnl ${day.takeItSignals.pnl >= 0 ? 'positive' : 'negative'}`}>
                    {formatPoints(day.takeItSignals.pnl)}
                  </span>
                </>
              ) : (
                <span className="day-empty">—</span>
              )}
            </span>
          </div>
        ))}
        {dayStats.length === 0 && (
          <div className="calendar-empty">No trading data available</div>
        )}
      </div>
    </div>
  );
};

// Log Tab Component
type DirectionFilter = 'all' | 'bullish' | 'bearish';
type ResultFilter = 'all' | 'unskipped' | 'win' | 'loss' | 'open' | 'skip';
type SessionFilter = 'all' | 'asian' | 'london' | 'newyork' | 'overnight';
type TakeItFilter = 'all' | 'takeit' | 'below';
type MlFilter = 'all' | 'take' | 'skip';

const LogTab: React.FC<{
  stats: WccStats;
  formatPoints: (v: number) => string;
}> = ({ stats, formatPoints }) => {
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>('all');
  const [resultFilter, setResultFilter] = useState<ResultFilter>('unskipped');
  const [sessionFilter, setSessionFilter] = useState<SessionFilter>('all');
  const [takeItFilter, setTakeItFilter] = useState<TakeItFilter>('all');
  const [mlFilter, setMlFilter] = useState<MlFilter>('all');
  const [confluenceFilter, setConfluenceFilter] = useState<string>('all');

  // Get unique confluence symbols from trades
  const allConfluenceSymbols = useMemo(() => {
    const symbols = new Set<string>();
    (stats.trades || []).forEach(t => {
      (t.confluenceSymbols || []).forEach(s => symbols.add(s));
    });
    return Array.from(symbols).sort();
  }, [stats.trades]);

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'America/Los_Angeles',
    });
  };

  // Filter and sort trades
  const filteredTrades = useMemo(() => {
    let trades = [...(stats.trades || [])];

    // Apply direction filter (backend uses LONG/SHORT, filter uses bullish/bearish)
    if (directionFilter !== 'all') {
      const dirMap: Record<string, string> = { bullish: 'LONG', bearish: 'SHORT' };
      trades = trades.filter(t => t.direction === dirMap[directionFilter]);
    }

    // Apply result filter (outcome is uppercase from backend: WIN, LOSS, OPEN)
    if (resultFilter !== 'all') {
      if (resultFilter === 'skip') {
        trades = trades.filter(t => t.skipped);
      } else if (resultFilter === 'unskipped') {
        trades = trades.filter(t => !t.skipped);
      } else {
        trades = trades.filter(t => !t.skipped && t.outcome.toLowerCase() === resultFilter);
      }
    }

    // Apply session filter
    if (sessionFilter !== 'all') {
      trades = trades.filter(t => t.session === sessionFilter);
    }

    // Apply Take It filter (based on takeItScore from API)
    const threshold = stats.takeItThreshold || 80;
    if (takeItFilter !== 'all') {
      if (takeItFilter === 'takeit') {
        trades = trades.filter(t => t.takeItScore > threshold);
      } else {
        trades = trades.filter(t => t.takeItScore <= threshold);
      }
    }

    // Apply ML filter
    if (mlFilter !== 'all') {
      if (mlFilter === 'take') {
        trades = trades.filter(t => t.mlRecommendation === 'TAKE');
      } else {
        trades = trades.filter(t => t.mlRecommendation === 'SKIP');
      }
    }

    // Apply confluence symbol filter
    if (confluenceFilter !== 'all') {
      trades = trades.filter(t =>
        t.confluenceSymbols && t.confluenceSymbols.includes(confluenceFilter)
      );
    }

    // Sort by timestamp descending (date filtering is done globally)
    return trades.sort((a, b) => b.timestamp - a.timestamp);
  }, [stats.trades, stats.takeItThreshold, directionFilter, resultFilter, sessionFilter, takeItFilter, mlFilter, confluenceFilter]);

  // Define columns for DataTable
  const columns = useMemo<ColumnDef<TradeResult, unknown>[]>(() => [
    {
      accessorKey: 'timestamp',
      header: 'Time',
      size: 110,
      cell: ({ getValue }) => (
        <span className="dt-mono">{formatTime(getValue() as number)}</span>
      ),
    },
    {
      accessorKey: 'direction',
      header: 'Dir',
      size: 50,
      cell: ({ getValue }) => {
        const dir = getValue() as string;
        const isLong = dir === 'LONG';
        return (
          <span className={isLong ? 'dt-positive' : 'dt-negative'}>
            {isLong ? '▲' : '▼'}
          </span>
        );
      },
    },
    {
      accessorKey: 'entryPrice',
      header: 'Entry',
      size: 85,
      cell: ({ getValue }) => (
        <span className="dt-mono">{(getValue() as number).toFixed(2)}</span>
      ),
    },
    {
      accessorKey: 'exitPrice',
      header: 'Exit',
      size: 85,
      cell: ({ getValue }) => {
        const v = getValue() as number | null;
        return <span className="dt-mono">{v ? v.toFixed(2) : '—'}</span>;
      },
    },
    {
      accessorKey: 'outcome',
      header: 'Result',
      size: 70,
      cell: ({ row }) => {
        const trade = row.original;
        if (trade.skipped) {
          return <span className="dt-badge warning">SKIP</span>;
        }
        const outcome = trade.outcome;
        const badgeClass = outcome === 'win' ? 'success' : outcome === 'loss' ? 'error' : 'info';
        return <span className={`dt-badge ${badgeClass}`}>{outcome.toUpperCase()}</span>;
      },
    },
    {
      accessorKey: 'pnl',
      header: 'P&L',
      size: 80,
      cell: ({ row }) => {
        const trade = row.original;
        if (trade.skipped) return <span className="dt-muted">—</span>;
        const pnl = trade.pnl;
        return (
          <span className={`dt-mono ${pnl >= 0 ? 'dt-positive' : 'dt-negative'}`}>
            {formatPoints(pnl)}
          </span>
        );
      },
    },
    {
      accessorKey: 'confidence',
      header: 'Sig%',
      size: 50,
      cell: ({ getValue }) => {
        const v = getValue() as number | null;
        if (v == null) return <span className="dt-muted">—</span>;
        return <span className="dt-mono">{Math.round(v)}</span>;
      },
    },
    {
      accessorKey: 'confluenceScore',
      header: 'CF',
      size: 50,
      cell: ({ getValue }) => {
        const v = getValue() as number | null;
        if (v == null) return <span className="dt-muted">—</span>;
        return (
          <span className={`dt-mono ${v > 0 ? 'dt-positive' : v < 0 ? 'dt-negative' : ''}`}>
            {v > 0 ? '+' : ''}{v}
          </span>
        );
      },
    },
    {
      accessorKey: 'confluenceSymbols',
      header: 'Symbols',
      size: 100,
      cell: ({ getValue }) => {
        const symbols = getValue() as string[] | null;
        if (!symbols || symbols.length === 0) return <span className="dt-muted">—</span>;
        return <span className="dt-mono confluence-symbols">{symbols.join(', ')}</span>;
      },
    },
    {
      id: 'takeItScore',
      header: 'TI',
      size: 50,
      cell: ({ row }) => {
        const score = row.original.takeItScore;
        const threshold = stats.takeItThreshold || 80;
        const isTakeIt = score > threshold;
        return (
          <span className={`dt-mono ${isTakeIt ? 'dt-positive' : ''}`}>
            {Math.round(score)}
          </span>
        );
      },
    },
    {
      accessorKey: 'mlScore',
      header: 'ML',
      size: 50,
      cell: ({ row }) => {
        const score = row.original.mlScore;
        const rec = row.original.mlRecommendation;
        if (score == null) return <span className="dt-muted">—</span>;
        const isTake = rec === 'TAKE';
        return (
          <span className={`dt-mono ${isTake ? 'dt-ml-take' : 'dt-ml-skip'}`}>
            {Math.round(score)}
          </span>
        );
      },
    },
    {
      accessorKey: 'session',
      header: 'Session',
      size: 75,
      cell: ({ getValue }) => <span>{getValue() as string}</span>,
    },
    {
      accessorKey: 'skipReason',
      header: 'Skip',
      size: 90,
      cell: ({ row }) => {
        const reason = row.original.skipReason;
        if (!reason) return <span className="dt-muted">—</span>;
        return <span className="dt-badge warning">{reason}</span>;
      },
    },
  ], [formatPoints, stats.takeItThreshold]);

  // Get row class for coloring
  const getRowClassName = (trade: TradeResult) => {
    if (trade.skipped) return 'log-row-skipped';
    if (trade.outcome === 'win') return 'log-row-win';
    if (trade.outcome === 'loss') return 'log-row-loss';
    return '';
  };

  return (
    <div className="wcc-stats-log">
      {/* Filters */}
      <div className="log-filters">
        <div className="log-filter-group">
          <label>Direction</label>
          <select
            value={directionFilter}
            onChange={(e) => setDirectionFilter(e.target.value as DirectionFilter)}
          >
            <option value="all">All</option>
            <option value="bullish">▲ Bullish</option>
            <option value="bearish">▼ Bearish</option>
          </select>
        </div>

        <div className="log-filter-group">
          <label>Result</label>
          <select
            value={resultFilter}
            onChange={(e) => setResultFilter(e.target.value as ResultFilter)}
          >
            <option value="all">All</option>
            <option value="unskipped">Traded</option>
            <option value="win">Win</option>
            <option value="loss">Loss</option>
            <option value="open">Open</option>
            <option value="skip">Skip</option>
          </select>
        </div>

        <div className="log-filter-group">
          <label>Session</label>
          <select
            value={sessionFilter}
            onChange={(e) => setSessionFilter(e.target.value as SessionFilter)}
          >
            <option value="all">All</option>
            <option value="asian">Asian</option>
            <option value="london">London</option>
            <option value="newyork">New York</option>
            <option value="overnight">Overnight</option>
          </select>
        </div>

        <div className="log-filter-group">
          <label>Take It</label>
          <select
            value={takeItFilter}
            onChange={(e) => setTakeItFilter(e.target.value as TakeItFilter)}
          >
            <option value="all">All</option>
            <option value="takeit">Take It ({stats.takeItThreshold || 80}+)</option>
            <option value="below">Below</option>
          </select>
        </div>

        <div className="log-filter-group">
          <label>ML</label>
          <select
            value={mlFilter}
            onChange={(e) => setMlFilter(e.target.value as MlFilter)}
          >
            <option value="all">All</option>
            <option value="take">ML TAKE</option>
            <option value="skip">ML SKIP</option>
          </select>
        </div>

        {allConfluenceSymbols.length > 0 && (
          <div className="log-filter-group">
            <label>Confluence</label>
            <select
              value={confluenceFilter}
              onChange={(e) => setConfluenceFilter(e.target.value)}
            >
              <option value="all">All</option>
              {allConfluenceSymbols.map(sym => (
                <option key={sym} value={sym}>{sym}</option>
              ))}
            </select>
          </div>
        )}

        <div className="log-count">
          {filteredTrades.length} / {stats.trades?.length || 0} signals
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={filteredTrades}
        columns={columns}
        compact
        stickyHeader
        maxHeight="450px"
        emptyMessage="No signals match filters"
        rowClassName={getRowClassName}
        initialSorting={[{ id: 'timestamp', desc: true }]}
      />
    </div>
  );
};

export default WccHistoricalStatsModal;
